import SEO from "@components/SEO";
import { createBreadcrumbStructuredData } from "../utils/structuredData";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Terms and Conditions | Ikhlas Tours"
        description="Ikhlas Tours terms and conditions covering rates, deposits, payments, cancellation policy, insurance and changes for our New Zealand tour packages."
        path="/terms"
        structuredData={createBreadcrumbStructuredData([
          { name: "Home", url: "/" },
          { name: "Terms", url: "/terms" },
        ])}
      />
      {/* Hero Section */}
      <section className="py-16 bg-linear-to-r from-primary to-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            Terms and Conditions
          </h1>
          <p className="text-xl text-primaryText">
            Please read our terms and conditions carefully
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="prose prose-lg max-w-none">
              {/* RATES */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-primaryText mb-4">
                  RATES
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  All rates quoted are 15% GST (Goods and Services Tax)
                  inclusive. Rates vary from time to time except those of which
                  booking fees have been received.
                </p>
              </section>

              {/* DEPOSITS AND PAYMENTS */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-primaryText mb-4">
                  Deposits and Payments
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  A deposit of 20% is required as a booking fee for car/MPV
                  packages and 10% deposit for motorhome packages.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Full payment of 80% balance for car/MPV packages must be made
                  within 30 days of arrival date. For motorhome packages, full
                  payment of 80% balance must be made within 40 days of arrival
                  date.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Deposits will not be refunded once all bookings for
                  accommodation and car/MPV/motorhome have been made.
                </p>
              </section>

              {/* METHODS OF PAYMENT */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-primaryText mb-4">
                  Methods of Payment
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Payment can be made via online bank transfer or WISE into our
                  New Zealand bank account. (Recommended payment is via WISE for
                  better exchange rates.)
                </p>
              </section>

              {/* CANCELLATION POLICY */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-primaryText mb-4">
                  Cancellation Policy
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Customers may have to cancel their reservation due to any
                  unforeseen circumstances. It should be noted when
                  accommodation and vehicle reservations were made we have to
                  pay the cost of the bookings to secure the accommodation and
                  vehicle required from our suppliers.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The terms of our cancellation policy after bookings have been
                  confirmed are as follows:
                </p>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-primaryText mb-3">
                    1) Accommodation and car/MPV packages
                  </h3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>
                      Any cancellation made 40 days or more prior to arrival, no
                      extra charges apply. (Deposits will not be refunded)
                    </li>
                    <li>
                      Any cancellation made less than 40 days or less prior to
                      arrival, an administration fee of NZD300 per person apply
                      including the cost charged by our vehicle and
                      accommodation suppliers.
                    </li>
                    <li>
                      Any cancellation made 10 days or less prior to arrival or
                      no show, full rates will apply.
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-primaryText mb-3">
                    2) Motorhome packages
                  </h3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>
                      Any cancellation made 50 days or more prior to arrival, no
                      extra charges apply. (Deposits will not be refunded)
                    </li>
                    <li>
                      Any cancellation made 50 days or less prior to arrival, an
                      administration fee of NZD300 per person apply including
                      the cost charged by our vehicle and service suppliers.
                    </li>
                    <li>
                      Any cancellation made 20 days or less prior to arrival or
                      no show, full rates will apply.
                    </li>
                  </ul>
                </div>
              </section>

              {/* INSURANCE */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-primaryText mb-4">
                  Insurance
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We advise our customers to take travel insurance before
                  travelling. Insurance companies have different terms and
                  conditions to protect the traveller. Please check the
                  conditions they provide.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  All vehicles that we provide include maximum insurance
                  provided by our suppliers.
                </p>
              </section>

              {/* CHANGE OF DATES AND OTHER DETAILS */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-primaryText mb-4">
                  Change of Dates and Other Details
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  You may want to change the dates and other details of the
                  package due to personal reasons. Any change of dates and other
                  details of the package which have been agreed upon may incur
                  additional costs.
                </p>
              </section>

              {/* DISCLAIMER */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-primaryText mb-4">
                  Disclaimer
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Ikhlas Tours Limited reserves the right to change package
                  rates from time to time, except packages which have been
                  booked.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If there are any changes in the travel plan that is beyond our
                  control, some services may be offered compensation based on
                  our mutual agreement.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Ikhlas Tours Limited will not be held liable for any claims,
                  fines, infringement or payment resulting from damage to public
                  property or the property of our suppliers due to negligence or
                  violations of any traffic rules and regulations.
                </p>
              </section>

              {/* SINCERE AND HONEST */}
              <section>
                <h2 className="text-2xl font-bold text-primaryText mb-4">
                  Sincere and Honest
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  In line with our company's name, the services we offer are
                  based on the principle of sincerity and honesty. We try to
                  give all relevant details about our services and packages. Our
                  products are subject to the New Zealand Fair Trading Act,
                  which focuses on the principle of honesty and fairness in
                  business.
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
